export interface GameConfig {
  startingHp: number;
  ringSlots: number;
  startingHandSize: number;
  minDeckSize: number;
  maxCopiesPerCard: number;
  supportPowerBonus: number;
  unitedAttackCost2: number;
  unitedAttackCost3: number;
  aiTurnDelay: number;  // ms delay between AI actions for visibility
  aiThinkingDelayMs: number;  // ms the AI must be thinking before the "Thinking…" indicator appears
  combatAnimationDelay: number;  // ms between each attacker's animation beat
  combatPreviewDelay: number;    // ms to show arrows/badges before combat resolves
  coinFlipArtTimeoutMs: number;  // max ms to wait for opponent art to load before sliding in regardless
  baseRalliesPerTurn: number;
  baseSupportsPerTurn: number;
  maxHandSize: number;
  maxDecks: number;
  customPackPollIntervalMs: number;
  customPackJobExpireMs: number;
  // ── Voice lines (Phase 2 dossier display) ──────────────────────
  /** Probability (0–1) that the `played` trigger surfaces a bubble on
   *  card play. Applied per played card. Opponent intro and the end-
   *  game interstitial scenes always play if lines exist; they aren't
   *  gated. */
  voiceLinePlayedProb: number;
  /** Probability (0–1) that the combat-pair triggers (combat_engage /
   *  kill_other / die) actually surface bubbles for that pair. The
   *  combat-pair-per-turn rate limit already restricts combat speech
   *  to one exchange per turn, so this gate defaults to 1.0 (always
   *  fire). Dial back if combat lines feel overwhelming in playtest. */
  voiceLineCombatProb: number;
  /** Probability (0–1) for united-attack combat triggers. Defaults to
   *  1.0 — a 2- or 3-character coordinated attack is a significant
   *  moment that warrants always getting a voice line. Independent of
   *  `voiceLineCombatProb` so you can dial individual combat speech
   *  down without losing the climactic united-attack moments. */
  voiceLineUnitedCombatProb: number;
  /** Probability (0–1) that combat RESOLUTION (kill_other / die /
   *  defender's combat_engage) produces a single bubble across all
   *  combat pairs. Distinct from voiceLineCombatProb which gates the
   *  staging-time combat_engage from the attacker. Lower default so
   *  resolution feels punchy rather than chatty — there's already a
   *  staging-time bubble preceding it. */
  voiceLineCombatResolveProb: number;
  /** Probability (0–1) that a non-combat death (action card damage,
   *  activated / triggered / passive ability KO, or cascade KO from
   *  a combat passive losing its provider) produces a `die` bubble.
   *  Independent roll from `voiceLineCombatResolveProb` — they can
   *  both fire in the same combat dispatch (e.g. a normal kill plus a
   *  cascade KO), producing up to two bubbles. */
  voiceLineNonCombatDeathProb: number;
  /** Probability (0–1) that a `support` action surfaces the supporter's
   *  `support` voice line (anchored above the recipient — the supporter
   *  is on its way to the discard). Supports happen multiple times per
   *  turn in some decks; the gate keeps the speech from feeling chatty. */
  voiceLineSupportProb: number;
  /** Milliseconds the bubble stays fully visible after fade-in. */
  voiceLineDwellMs: number;
  /** Fade-in / fade-out duration for bubbles. */
  voiceLineFadeMs: number;
  /** Fraction-of-maxHp threshold below which opponent_surprised /
   *  opponent_gloat trigger when their side's ring is also empty. */
  surprisedGloatHpThreshold: number;
  /** Slide-in animation duration for each scene of the end-game
   *  interstitial (responsible card AND opponent profile). */
  interstitialSlideMs: number;
}

export const DEFAULT_CONFIG: GameConfig = {
  startingHp: 3000,
  ringSlots: 3,
  startingHandSize: 5,
  minDeckSize: 50,
  maxCopiesPerCard: 3,
  supportPowerBonus: 300,
  unitedAttackCost2: 5,
  unitedAttackCost3: 10,
  aiTurnDelay: 600,
  aiThinkingDelayMs: 1000,
  combatAnimationDelay: 700,
  combatPreviewDelay: 1500,
  coinFlipArtTimeoutMs: 500,
  baseRalliesPerTurn: 1,
  baseSupportsPerTurn: 1,
  maxHandSize: 7,
  maxDecks: 3,
  customPackPollIntervalMs: 500,
  customPackJobExpireMs: 1000 * 60 * 30,
  voiceLinePlayedProb: 0.6,
  voiceLineCombatProb: 0.6,
  voiceLineUnitedCombatProb: 1.0,
  voiceLineCombatResolveProb: 0.6,
  voiceLineNonCombatDeathProb: 0.8,
  voiceLineSupportProb: 0.6,
  voiceLineDwellMs: 2500,
  voiceLineFadeMs: 300,
  surprisedGloatHpThreshold: 0.20,
  interstitialSlideMs: 300,
};

let activeConfig: GameConfig = { ...DEFAULT_CONFIG };

export function getConfig(): GameConfig {
  return activeConfig;
}

export function setConfig(overrides: Partial<GameConfig>): void {
  activeConfig = { ...activeConfig, ...overrides };
}
